import React, { FormEvent, useState, useContext, useEffect } from 'react';
import './Login.css';
import logo from '../../../assets/logo-icon.svg';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SlButton } from '@shoelace-style/shoelace/dist/react/index.js';
import { SlInput } from '@shoelace-style/shoelace/dist/react/index.js';
import { AppContext, appContext } from '../../../context/AppContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import * as icons from '../../../constants/icons';
import { RecaptchaPolicies } from '../../base/RecaptchaPolicies/RecaptchaPolicies';

const Login = () => {
	return (
		<GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY}>
			<LoginForm />
		</GoogleReCaptchaProvider>
	);
};

const LoginForm = () => {
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { t, i18n } = useTranslation();
	const [searchParams] = useSearchParams();
	const { executeRecaptcha } = useGoogleReCaptcha();

	const { login, showStatus } = useContext(appContext as React.Context<AppContext>);

	useEffect(() => {
		const hasResetPassword = searchParams.get('password-reset') === 'true';
		if (hasResetPassword) {
			showStatus({
				variant: 'success',
				icon: icons.OK,
				text: t('You can now log in with your new password'),
			});
		}
	}, []);

	const onLogin = async (e: FormEvent) => {
		e.preventDefault();
		setIsLoading(true);

		if (!executeRecaptcha) {
			const message = 'Error: reCAPTCHA uninitialized';
			Sentry.captureException(message);
			console.error(message);
			setIsLoading(false);
			return;
		}
		const captchaToken = await executeRecaptcha('login');

		try {
			await login(email, password, captchaToken);
		} catch (err) {
			setIsLoading(false);
			return;
		}
		setIsLoading(false);
	};

	const onEmailChange = (e: any) => {
		setEmail(e.currentTarget.value);
	};

	const onPaswordChange = (e: any) => {
		setPassword(e.currentTarget.value);
	};

	return (
		<div className='login'>
			<div className='login__wrapper'>
				<img className='login__logo' src={logo} />
				<div className='login__container'>
					<div className='login__heading'>
						<h1 className='login__heading-title'>{t('Login')}</h1>
						<div className='login__heading-sub-title'>{t('Dealer Area')}</div>
					</div>
					<form className='login__form' onSubmit={onLogin}>
						<SlInput
							type='email'
							className='login__email'
							inputMode='email'
							label={t('Email')}
							onSlInput={onEmailChange}
							name='email'
							value={email}
							required
						/>
						<SlInput
							type='password'
							passwordToggle={true}
							label={t('Password')}
							className='login__password'
							onSlInput={onPaswordChange}
							name='password'
							value={password}
							required
						/>
						<div className='login__password-reset'>
							<a
								href={`${import.meta.env.VITE_RESET_PASSWORD_URL.replace(
									'{lang}',
									i18n.language,
								)}?is-merchant=true`}
							>
								{t('Forgot your password?')}
							</a>
						</div>
						<RecaptchaPolicies />
						<SlButton
							className='login__button'
							type='submit'
							variant='primary'
							loading={isLoading}
						>
							{t('Login')}
						</SlButton>
					</form>
				</div>
			</div>
		</div>
	);
};

export { Login };
